import * as Sentry from '@sentry/browser';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import { getDSN, getParams } from './sentryContext';
import { printError, printMessage } from './localSentry';

/**
 * @var {Sentry}
 * */
let sentryInstance = null;

/**
 * Remember errors that has been reported to Sentry already
 * to reduce the amount of spamming. Especially useful in case
 * a chat endpoint fails and we continuously report the same errors,
 * until the user closes its browser.
 * */
const alreadyReportedErrors = new Set();

/**
 * Builds an "error signature" (stored in alreadyReportedErrors)
 * with sufficient uniqueness, such that we do not filter out too many
 * similar errors
 * @return {string|null} The signature or null. Null is returned when we cannot generate a usable signature.
 * */
const buildErrorSignature = (error) => {
  const { response = null } = error;
  // Differentiate the error message by URL, message and response-status
  if (response) {
    return `${error.message.toLowerCase()}-${response.status}-${response.url.toLowerCase()}`;
  }

  // If we have no error message then we cannot really create an error signature
  if (!error.message) {
    console.warn('Error is missing message. Couldn\'t build error signature for deduplication', error);
    return null;
  }

  return error.message.toLowerCase();
};

// eslint-disable-next-line no-undef
const initSentry = () => {
  const dsn = getDSN();

  Sentry.init({
    ...getParams(),
    integrations: [new TracingIntegrations.BrowserTracing()],
    // This will capture around 10% of transactions.
    // We get 100K transactions included in our plan,
    // so we do not want to capture too much unless necessary
    tracesSampleRate: 0.1,
    dsn,
    allowUrls: [
      'ibok.no',
      'staging.ibok.no',
      'www.google.com',
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (!error) {
        return event;
      }

      const errorMessage = buildErrorSignature(error);
      // Explicitly match on null, because an empty string is still a valid signature
      if (errorMessage === null) {
        // No signature could be generated, so we should always notify those errors
        return event;
      }

      // No point in reporting the same error
      if (alreadyReportedErrors.has(errorMessage)) {
        // eslint-disable-next-line no-console
        console.info(`Error has already been reported to Sentry. Key: "${errorMessage}". Skipping...`);
        return null;
      }

      alreadyReportedErrors.add(errorMessage);
      return event;
    },
  });

  return Sentry;
};

// eslint-disable-next-line import/prefer-default-export
export const getSentry = () => {
  if (!sentryInstance) {
    sentryInstance = initSentry();
  }

  return sentryInstance;
};

/**
 * @param {Sentry} sentry
 * @param {function} scoped
 * @param {object} user
 * @param {object} tags
 * @param {object} extras
 * */
const withUserScope = (sentry, scoped, user = null, tags = null, extras = null) => {
  sentry.withScope((scope) => {
    if (user) {
      scope.setUser(user);
    }

    if (tags) {
      scope.setTags(tags);
    }

    if (extras) {
      scope.setExtras(extras);
    }

    scoped(scope, sentry);
  });
};

/**
 * Wrapper around sentry.captureMessage, which reports locally in development
 * and to Sentry in production.
 *
 * @param {string} message The message to report
 * @param {string} severity "debug", "info", "warning", "error"
 * @param {object} user Data used to identify a "user"
 * @param {object} tags
 * @param {object} extras Any additional context data
 * */
export const captureMessage = (message, severity = '', user = null, tags = null, extras = null) => {
  const sentry = getSentry();

  if (!sentry) {
    printMessage(message, severity, user, tags, extras);
    return;
  }

  withUserScope(sentry, (scope, s) => {
    s.captureMessage(message, severity);
  }, user, tags, extras);
};

/**
 * Reports and exception to Sentry in production. In development is the
 * error reported locally in console.
 *
 * @param {string} error The error to report
 * @param {object} user Data used to identify a "user"
 * @param {object} tags
 * @param {object} extras Any additional context data
 * */
export const captureException = (error, user = null, tags = null, extras = null) => {
  const sentry = getSentry();

  if (!sentry) {
    printError(error, user, tags, extras);
    return;
  }

  withUserScope(sentry, (scope, s) => {
    s.captureException(error);
  }, user, tags, extras);

  sentry.withScope((scope) => {
    if (user) {
      scope.setUser(user);
    }

    if (tags) {
      scope.setTags(tags);
    }

    if (extras) {
      scope.setExtras(extras);
    }

    sentry.captureException(error);
  });
};
